import { z } from 'zod'

import { ActivityType } from '@forto-dev/feed-v2-models'
import { RuleID } from '@forto-dev/insights-engine-models'
import { CustomerAccountNotificationResponse } from '@forto-dev/notification-center-models'
import { Contact } from '@forto-dev/subscriptions-models'

import { NotificationContextSchema } from '@/features/notifications'
import { TransportType } from '@/features/shipments'
import { isDateTime } from '@/utils/localisation/i18n/date'

export const CHANGED_FIELDS = [
  'containerNumber',
  'containerNumbers',
  'containerCustomerReferences',
  'sealNumbers',
  'customerReference',
  'additionalInformation',
  'incoTerms',
  'hblNumber',
  'hblType',
  'pickupDetails',
  'deliveryDetails',
  'consigneeContact',
  'shipperContact',
  'containerTags',
] as const

export type ChangedField = (typeof CHANGED_FIELDS)[number]

export const isValidChangedField = (input: unknown): input is ChangedField =>
  typeof input === 'string' && CHANGED_FIELDS.some((item) => item === input)

export const ACTIVITY_FILTER = ['deviation', 'deviationRisk', 'messages', 'communication', 'updates', 'files'] as const

export type FeedActivityFilter = (typeof ACTIVITY_FILTER)[number]

const ACTIVITY_FILTERS_DEVIATION = [ActivityType.DeviationInsight]
const ACTIVITY_FILTERS_DEVIATION_RISK = [ActivityType.DeviationRiskInsight]
const ACTIVITY_FILTERS_MESSAGES = [ActivityType.MessageFeedV1]
const ACTIVITY_FILTERS_COMMUNICATION = [ActivityType.CommunicationFeedV1]
const ACTIVITY_FILTERS_FILES = [ActivityType.DocumentUploadFeedV1]
const ACTIVITY_FILTERS_UPDATES = Object.values(ActivityType).filter(
  (item) =>
    ![
      ...ACTIVITY_FILTERS_DEVIATION,
      ...ACTIVITY_FILTERS_DEVIATION_RISK,
      ...ACTIVITY_FILTERS_MESSAGES,
      ...ACTIVITY_FILTERS_COMMUNICATION,
      ...ACTIVITY_FILTERS_FILES,
    ].includes(item)
)

export const ACTIVITY_FILTERS: Record<FeedActivityFilter, ActivityType[]> = {
  deviation: ACTIVITY_FILTERS_DEVIATION,
  deviationRisk: ACTIVITY_FILTERS_DEVIATION_RISK,
  messages: ACTIVITY_FILTERS_MESSAGES,
  communication: ACTIVITY_FILTERS_COMMUNICATION,
  files: ACTIVITY_FILTERS_FILES,
  updates: ACTIVITY_FILTERS_UPDATES,
}

export type APIFeedActor = {
  type: 'Service' | 'Person'
  id: string
  fullName?: string
  avatar?: string
  email?: string
}

export type APIFeedItem = {
  id: string
  updatedAt?: string
  activity: {
    actor: APIFeedActor
    createdAt: string
    aggregation?: { role: 'member' | 'aggregate' }
    customField: {
      ruleID?: RuleID
      userIds?: string[]
      freightieIds?: string[]
      code?: string
      actual?: boolean
      confirmed?: boolean
      reason?: string
      tag?: string
      type?: string
      date?: string
      utcOffsetHours?: number
      affectedSubscriberId?: string
      affectedSubscriberName?: string
      affectedContact?: Contact
      feedV1Activity?: {
        _id?: string
        createdAt: string
        updatedAt: string
        internal: boolean
        type: string
        action?: 'created' | 'updated' | 'deleted'
        freightieAuthor_id?: string | null
        document_id?: string
        name?: string
        content?: string
        source?: string
        role?: string
        referenceId: string
        userAuthor_id: string
        changedFields?: Partial<Record<ChangedField, string>>
      }
    } & Record<string, unknown>
    object: {
      type: string
      id: string
      trackables?: {
        id: string
        containerNumber?: string
        cargo: {
          quantity: number
          grossWeight: number
          volume: number
          width: number
          height: number
          length: number
          commodityType: string
          type: TransportType
        }[]
      }[]
    }
    type: ActivityType
    to: never[]
  }
  feedInfo: {
    customerID: string
    entityID: string
    entityType: string
    groupType: string
    activityVisibility: string
    context?: CustomerAccountNotificationResponse['context']
  }
}

export type APIFeed = {
  orderByDesc: string
  page: number
  pageSize: number
  entityID: string | string[]
  orderBy: string
  results: APIFeedItem[]
  total: number
  activityCountByType: {
    type: ActivityType
    count: number
  }[]
}

const ActivityTypeEnum = z.nativeEnum(ActivityType)
type ActivityTypeEnum = z.infer<typeof ActivityTypeEnum>

export const FeedItemSchema = z.object({
  id: z.string(),
  updatedAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  feedInfo: z.object({
    entityID: z.string(),
    activityVisibility: z.string(),
    customerID: z.string(),
    context: NotificationContextSchema
      // @see https://github.com/freight-hub/ship-v2/blob/6edeff69521608818e3095a7722df21e1aa4932a/src/features/notifications/domain/notification.ts#L193 for why passthrough
      .passthrough()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
  activity: z.object({
    type: ActivityTypeEnum,
    createdAt: z.coerce.date(),
    actor: z.object({
      type: z.enum(['Service', 'Person']),
      avatar: z
        .string()
        .nullish()
        .transform((data) => data ?? ''),
      fullName: z
        .string()
        .nullish()
        .transform((data) => data ?? ''),
    }),
    object: z.object({
      trackables: z
        .array(
          z
            .object({
              id: z.string(),
              containerNumber: z
                .string()
                .nullish()
                .transform((data) => data ?? ''),
            })
            .nullish()
            .transform((data) => data ?? undefined)
        )
        .nullish()
        .transform((data) => data ?? undefined),
      id: z
        .string()
        .nullish()
        .transform((data) => data ?? ''),
      type: z
        .string()
        .nullish()
        .transform((data) => data ?? ''),
    }),
    customField: z
      .object({
        sourceActivityID: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        ruleID: z
          .nativeEnum(RuleID)
          .or(z.string())
          .nullish()
          .transform((data) => data ?? undefined),
        date: z.preprocess(
          (date) => {
            return typeof date === 'string' ? { value: new Date(date), isDateTime: isDateTime(date) } : undefined
          },
          z
            .object({ value: z.date(), isDateTime: z.boolean() })
            .nullish()
            .transform((data) => data ?? undefined)
        ),
        changedFields: z
          .record(z.enum(CHANGED_FIELDS).or(z.string()), z.string())
          .nullish()
          .transform((data) => data ?? {}),
        feedV1Activity: z
          .object({
            _id: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
            content: z
              .string()
              .nullish()
              .transform((data) => data ?? ''),
            action: z
              .enum(['created', 'updated', 'deleted'])
              .nullish()
              .transform((data) => data ?? 'created'),
            role: z
              .string()
              .nullish()
              .transform((data) => data ?? ''),
            document_id: z
              .string()
              .nullish()
              .transform((data) => data ?? ''),
            name: z
              .string()
              .nullish()
              .transform((data) => data ?? ''),
            changedFields: z
              .record(z.enum(CHANGED_FIELDS).or(z.string()), z.string())
              .nullish()
              .transform((data) => data ?? {}),
          })
          .passthrough()
          .nullish()
          .transform((data) => data ?? undefined),
        fieldValues: z.any(),
        workflow: z.any(),
        affectedSubscriberName: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        affectedContact: z
          .object({
            contactPerson: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
          })
          .nullish()
          .transform((data) => data ?? undefined),
        reason: z
          .string()
          .nullish()
          .transform((data) => data ?? ''),
        actual: z
          // boolean for TP, string for customs activities, number for delay activities
          .union([z.number(), z.boolean(), z.string()])
          .nullish()
          .transform((data) => data ?? undefined),
        confirmed: z
          .boolean()
          .nullish()
          .transform((data) => data ?? undefined),
      })
      // @see https://github.com/freight-hub/ship-v2/blob/6edeff69521608818e3095a7722df21e1aa4932a/src/features/notifications/domain/notification.ts#L193 for why passthrough
      .passthrough(),
    aggregation: z
      .object({
        role: z.enum(['member', 'aggregate']),
      })
      .nullish()
      .transform((data) => data ?? undefined),
  }),
})

export type FeedItem = z.infer<typeof FeedItemSchema>

const formatActivityCountByType = (
  activityCountByType: { type: ActivityType; count: number }[]
): Partial<Record<ActivityType, number>> => {
  return activityCountByType.reduce((acc, curr) => ({ ...acc, [curr.type]: curr.count }), {})
}

export const FeedSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  results: z.array(FeedItemSchema),
  total: z.number(),
  // To transform array of objects [{ [activityType1]: count }, { [activityType2]: count }] to { activityType1: count, activityType2: count }
  activityCountByType: z.preprocess(
    (activityCountByType) => {
      return Array.isArray(activityCountByType) ? formatActivityCountByType(activityCountByType) : {}
    },
    z.record(ActivityTypeEnum, z.number())
  ),
})

export type Feed = z.infer<typeof FeedSchema>
